import React, { useEffect, useState } from "react";
import "./AnnouncementPopup.css";

const API_BASE =
    process.env.REACT_APP_API_BASE ??
    (process.env.NODE_ENV === "production"
        ? ""
        : "http://localhost:5000");

const getToken = () => {
    try {
        return localStorage.getItem("token") || "";
    } catch {
        return "";
    }
};

export default function AnnouncementAdmin() {
    const [items, setItems] = useState([]);
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({
        title: "",
        message: "",
        media_path: "",
        media_type: "",
    });
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState("");
    const [editingId, setEditingId] = useState(null); // 🆕 track edit mode

    const loadAll = async () => {
        setError("");
        try {
            const res = await fetch(`${API_BASE}/api/announcements`, {
                headers: { Authorization: `Bearer ${getToken()}` },
            });
            const data = await res.json();
            if (!res.ok || !data.ok) throw new Error(data?.error || "Load failed");
            setItems(data.rows || []);
        } catch (e) {
            setError(e.message);
            setItems([]);
        }
    };

    useEffect(() => {
        loadAll();
    }, []);

    const onUpload = async (file) => {
        if (!file) return;
        setUploading(true);
        setError("");
        try {
            const fd = new FormData();
            fd.append("file", file);
            const res = await fetch(`${API_BASE}/api/announcements/upload`, {
                method: "POST",
                headers: { Authorization: `Bearer ${getToken()}` },
                body: fd,
            });
            const data = await res.json();
            if (!res.ok || !data.ok) throw new Error(data?.error || "Upload failed");
            setForm((p) => ({
                ...p,
                media_path: data.path,
                media_type: data.mediaType,
            }));
        } catch (e) {
            setError(e.message);
        } finally {
            setUploading(false);
        }
    };

    const onSave = async () => {
        setBusy(true);
        setError("");
        try {
            const method = editingId ? "PUT" : "POST";
            const url = editingId
                ? `${API_BASE}/api/announcements/${editingId}`
                : `${API_BASE}/api/announcements`;

            const res = await fetch(url, {
                method,
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${getToken()}`,
                },
                body: JSON.stringify(form),
            });

            const data = await res.json();
            if (!res.ok || !data.ok)
                throw new Error(data?.error || "Save failed");

            setForm({ title: "", message: "", media_path: "", media_type: "" });
            setEditingId(null); // 🆕 exit edit mode
            await loadAll();
        } catch (e) {
            setError(e.message);
        } finally {
            setBusy(false);
        }
    };

    const onDelete = async (id) => {
        if (!window.confirm("Delete this announcement?")) return;
        try {
            const res = await fetch(`${API_BASE}/api/announcements/${id}`, {
                method: "DELETE",
                headers: { Authorization: `Bearer ${getToken()}` },
            });
            const data = await res.json();
            if (!res.ok || !data.ok) throw new Error(data?.error || "Delete failed");
            await loadAll();
        } catch (e) {
            setError(e.message);
        }
    };

    const onEdit = (item) => {
        // 🆕 populate form with selected item
        setEditingId(item.id);
        setForm({
            title: item.title,
            message: item.message,
            media_path: item.media_path || "",
            media_type: item.media_type || "",
        });
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const renderPreview = () => {
        if (!form.media_path) return null;
        const fullPath = `${API_BASE}${form.media_path}`;

        if (form.media_type === "image") {
            return (
                <img
                    alt="preview"
                    src={fullPath}
                    style={{
                        maxWidth: 280,
                        borderRadius: 8,
                        border: "1px solid #ddd",
                    }}
                />
            );
        }
        if (form.media_type === "video") {
            return (
                <video controls style={{ maxWidth: 320, borderRadius: 8 }}>
                    <source src={fullPath} />
                </video>
            );
        }
        if (form.media_type === "pdf") {
            return (
                <a href={fullPath} target="_blank" rel="noreferrer">
                    View PDF
                </a>
            );
        }
        return (
            <a href={fullPath} target="_blank" rel="noreferrer">
                Download file
            </a>
        );
    };

    return (
        <div className="report-section" style={{ marginTop: 16 }}>
            <h3 className="card-title">
                {editingId ? "✏️ Edit Announcement" : "📢 Create Announcement"}
            </h3>

            {error && (
                <div style={{ color: "#ff7676", marginBottom: 8 }}>{error}</div>
            )}

            {/* Form Section */}
            <div className="grid" style={{ marginTop: 8 }}>
                <div className="field">
                    <label>Title</label>
                    <input
                        value={form.title}
                        onChange={(e) =>
                            setForm((p) => ({ ...p, title: e.target.value }))
                        }
                    />
                </div>

                <div className="field" style={{ gridColumn: "1 / -1" }}>
                    <label>Message</label>
                    <textarea
                        rows={3}
                        value={form.message}
                        onChange={(e) =>
                            setForm((p) => ({ ...p, message: e.target.value }))
                        }
                    />
                </div>

                <div className="field media-upload-field">
                    <label>Upload Media (any type)</label>
                    <input
                        type="file"
                        onChange={(e) => onUpload(e.target.files?.[0])}
                        disabled={uploading}
                    />
                    {uploading && (
                        <div style={{ color: "goldenrod" }}>Uploading…</div>
                    )}
                </div>

                <div className="field announcement-action-field">
                    <button
                        className="btn-primary"
                        onClick={onSave}
                        disabled={busy}
                    >
                        {busy
                            ? "Saving…"
                            : editingId
                                ? "Update Announcement"
                                : "Upload & Save"}
                    </button>
                    {editingId && (
                        <button
                            className="btn-secondary"
                            onClick={() => {
                                setEditingId(null);
                                setForm({
                                    title: "",
                                    message: "",
                                    media_path: "",
                                    media_type: "",
                                });
                            }}
                        >
                            Cancel Edit
                        </button>
                    )}
                    {form.media_path && (
                        <button
                            className="btn-secondary"
                            onClick={() =>
                                setForm((p) => ({
                                    ...p,
                                    media_path: "",
                                    media_type: "",
                                }))
                            }
                        >
                            Clear File
                        </button>
                    )}
                </div>

                <div className="field" style={{ gridColumn: "1 / -1" }}>
                    <label>Preview</label>
                    <div className="preview-box">
                        {renderPreview() || (
                            <em style={{ color: "#aaa" }}>No file uploaded</em>
                        )}
                    </div>
                </div>
            </div>

            {/* Table Section */}
            <h4 style={{ marginTop: 18, color: "goldenrod" }}>
                Existing Announcements
            </h4>
            <div className="table-wrap" style={{ overflowX: "auto", marginTop: 8 }}>
                <table className="nice-table light-table" style={{ width: "100%" }}>
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Title</th>
                            <th>Message</th>
                            <th>Media</th>
                            <th>Type</th>
                            <th>Created</th>
                            <th style={{ textAlign: "center" }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.length === 0 ? (
                            <tr>
                                <td colSpan={7} style={{ textAlign: "center" }}>
                                    No announcements yet.
                                </td>
                            </tr>
                        ) : (
                            items.map((a) => {
                                const fullMedia = a.media_path
                                    ? `${API_BASE}${a.media_path}`
                                    : null;
                                return (
                                    <tr key={a.id}>
                                        <td>{a.id}</td>
                                        <td>{a.title}</td>
                                        <td style={{ whiteSpace: "pre-wrap" }}>
                                            {a.message}
                                        </td>
                                        <td>
                                            {fullMedia ? (
                                                a.media_type === "image" ? (
                                                    <img
                                                        src={fullMedia}
                                                        alt=""
                                                        style={{
                                                            width: 60,
                                                            height: 40,
                                                            objectFit: "cover",
                                                            borderRadius: 4,
                                                        }}
                                                    />
                                                ) : (
                                                    <a
                                                        href={fullMedia}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                    >
                                                        Open
                                                    </a>
                                                )
                                            ) : (
                                                "—"
                                            )}
                                        </td>
                                        <td>{a.media_type || "—"}</td>
                                        <td>
                                            {new Date(a.created_at).toLocaleString()}
                                        </td>
                                        <td
                                            style={{
                                                textAlign: "center",
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            <button
                                                className="btn-secondary"
                                                onClick={() => onEdit(a)}
                                            >
                                                Edit
                                            </button>{" "}
                                            <button
                                                className="btn-secondary danger"
                                                onClick={() => onDelete(a.id)}
                                            >
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
